import { UseLoading } from "@/common/composition/loading";
import { UseSearch } from "@/common/composition/search";
import {
  PopulationTypeHelper,
  POPULATION_TYPE,
  SensitivityLevelHelper,
} from "@/common/models";
import { SharedState } from "@/common/state";
import { computed, Ref, ref } from "@vue/composition-api";
import {
  FamiliarityEntityType,
  FamiliarityItem,
  FamiliarityStatus,
} from "./models";
import * as api from "./api";
import { UseCollection } from "./use-collection.composition";
import ManageTrustModalVue from "@/views/customize/in-motion/exposure-control/manage-trust-modal/ManageTrustModal.vue";
import { UseModal } from "@/components/root/modal";
import EventsModalVue from "@/components/modals/events-modal/EventsModal.vue";
import { AxisType, CoordinateInputTier3 } from "@/gql/types";
import { EventsModalProps } from "@/components/modals/events-modal/model";
import { ICONS } from "@/common/icons.map";
import { UseDropdownItems } from "@/common/composition/dropdown-items";
import { debounce } from "@/fp";
import { EventBus } from "@/eventbus";

interface FilterItem {
  id: string;
  name: string;
  checked: boolean;
}

//#region  FILTERS

const EMPTY_CHECK_ITEM: FilterItem = { id: "", name: "", checked: false };

const info_types: Ref<FilterItem[]> = ref([]);
const selected_info_type: Ref<FilterItem> = ref(EMPTY_CHECK_ITEM);
const set_selected_info_type = async (id: string) => {
  selected_info_type.value = info_types.value.find(
    (l) => l.id === id
  ) as FilterItem;
  await on_filters_updated();
};

const [
  affinity_levels,
  selected_affinity_level,
  set_selected_affinity_level,
] = UseDropdownItems(
  ["None", "Anomaly", "New", "Normal", "Strong"].map((d) => ({
    id: d.toLowerCase(),
    name: d,
  }))
);

const entity_types: Ref<FilterItem[]> = ref(
  PopulationTypeHelper.displayList([
    POPULATION_TYPE.EMPLOYEE,
    POPULATION_TYPE.ORGANIZATION,
    POPULATION_TYPE.PRIVATE_EMAIL,
    POPULATION_TYPE.DEPARTMENT,
  ]).map(({ id, icon, name }) => {
    return {
      id,
      name: name + "s",
      icon,
      checked: false,
    };
  })
);
const selected_entity_type: Ref<FilterItem> = ref(entity_types.value[0]);
const set_selected_entity_type = async (id: string) => {
  selected_entity_type.value = entity_types.value.find(
    (l) => l.id === id
  ) as FilterItem;
  await on_filters_updated();
};

const departments = computed(() => [
  {
    id: "any",
    name: "Any Department",
    checked: false,
  },
  ...Object.entries(SharedState.departments.value).map(([id, name]) => {
    return {
      id,
      name,
      checked: false,
    };
  }),
]);
const selected_department: Ref<FilterItem> = ref(departments.value[0]);
const set_selected_department = async (id: string) => {
  selected_department.value = departments.value.find(
    (l) => l.id === id
  ) as FilterItem;
  await on_filters_updated();
};

const sensitivity_levels: Ref<FilterItem[]> = ref(
  [
    [3, "Highly"],
    [2, "Medium"],
    [1, "Low"],
  ].map(([id, name]) => {
    return {
      id: id,
      name: name + " sensitive",
      checked: false,
    };
  })
);

const selected_sensitivity_level: Ref<FilterItem> = ref(
  sensitivity_levels.value[0]
);
const set_selected_sensitivity_level = async (id: string) => {
  selected_sensitivity_level.value = sensitivity_levels.value.find(
    (l) => l.id == id
  ) as FilterItem;
  await on_filters_updated();
};

const { query, results, isSearching } = UseSearch(async (q: string) => {
  return {
    cursor: "",
    objects: [
      {
        id: "1",
        name: "bubu",
      },
    ],
  };
});

const selected_virtual_label = computed(() => {
  const label = SharedState.virtualLabels.value.find(
    (l) =>
      // @ts-ignore
      SensitivityLevelHelper.floatToInt(l.sensitivity) ===
        selected_sensitivity_level.value.id &&
      l.parentId === selected_info_type.value.id
  );
  return label ? label.id : "?";
});

const sub_info_types_for_label = computed(() => {
  return [
    ...(
      SharedState.typesByCategories.value
        .get(selected_info_type.value.id)
        ?.filter(
          (l: any) =>
            // @ts-ignore
            l.sensitivityLevel === selected_sensitivity_level.value.id
        ) || []
    ).map((l: any) => l.id),
  ];
});

const [
  unmanaged_entities,
  unmanaged_entities_count,
  is_loading_unmanaged_entities,
  load_unmanaged_entities,
  clear_unmanaged_entities,
  has_more_unmanaged_results_to_load,
] = UseCollection<FamiliarityItem>(async (cursor: string) => {
  if (selected_entity_type.value.id !== POPULATION_TYPE.DEPARTMENT) {
    return await api.get_simplified_entities_for_status({
      status: "unmanaged",
      label_id: selected_virtual_label.value,
      entity_type: selected_entity_type.value.id as FamiliarityEntityType,
      department_id: selected_department.value.id,
      cursor: cursor,
    });
  }
  return await api.get_entities_for_status({
    status: "unmanaged",
    label_id: selected_virtual_label.value,
    department_id: selected_department.value.id,
    entity_type: selected_entity_type.value.id as FamiliarityEntityType,
    cursor: cursor,
  });
});

const unmanaged_entities_display_list = computed(() => {
  if (isSearching.value) {
    return results.value;
  } else {
    return unmanaged_entities.value;
  }
});

const [
  trusted_entities,
  trusted_entities_count,
  is_loading_trusted_entities,
  load_trusted_entities,
  clear_trusted_entities,
  has_more_trusted_results_to_load,
] = UseCollection<FamiliarityItem>(async (cursor: string) => {
  if (selected_entity_type.value.id !== POPULATION_TYPE.DEPARTMENT) {
    return await api.get_simplified_entities_for_status({
      status: "trusted",
      label_id: selected_virtual_label.value,
      entity_type: selected_entity_type.value.id as FamiliarityEntityType,
      department_id: selected_department.value.id,
      cursor: cursor,
    });
  }
  return await api.get_entities_for_status({
    status: "trusted",
    label_id: selected_virtual_label.value,
    department_id: selected_department.value.id,
    entity_type: selected_entity_type.value.id as FamiliarityEntityType,
    cursor: cursor,
  });
});

const [
  untrusted_entities,
  untrusted_entities_count,
  is_loading_untrusted_entities,
  load_untrusted_entities,
  clear_untrusted_entities,
  has_more_untrusted_results_to_load,
] = UseCollection<FamiliarityItem>(async (cursor: string) => {
  if (selected_entity_type.value.id !== POPULATION_TYPE.DEPARTMENT) {
    return await api.get_simplified_entities_for_status({
      status: "untrusted",
      label_id: selected_virtual_label.value,
      entity_type: selected_entity_type.value.id as FamiliarityEntityType,
      department_id: selected_department.value.id,
      cursor: cursor,
    });
  }
  return await api.get_entities_for_status({
    status: "untrusted",
    label_id: selected_virtual_label.value,
    department_id: selected_department.value.id,
    entity_type: selected_entity_type.value.id as FamiliarityEntityType,
    cursor: cursor,
  });
});

const last_filters_state = ref({
  info_type: "",
  sensitivity: "",
  entity_type: "",
});

const is_filter_changed = computed(() => {
  const { entity_type, info_type, sensitivity } = last_filters_state.value;
  const last_filter_str = [entity_type, info_type, sensitivity].join("_");
  const current_filter_str = [
    selected_entity_type.value.id,
    selected_info_type.value.id,
    selected_sensitivity_level.value.id,
  ].join("_");
  return last_filter_str != current_filter_str;
});

const clear_data = () => {
  clear_trusted_entities();
  clear_untrusted_entities();
  //  clear_viewer_only_entities();
  clear_unmanaged_entities();
};

const [is_loading_all, set_loading_all] = UseLoading(true).as_arr();

const on_filters_updated = debounce(250)(
  async (specific_status: FamiliarityStatus) => {
    set_loading_all(true);
    last_filters_state.value = {
      info_type: selected_info_type.value.id,
      sensitivity: selected_sensitivity_level.value.id,
      entity_type: selected_entity_type.value.id,
    };
    clear_data();
    if (specific_status) {
      await load_data_for_specific_status(specific_status);
    } else {
      await Promise.all([
        load_trusted_entities(),
        load_unmanaged_entities(),
        load_untrusted_entities(),
      ]);
    }
    set_loading_all(false);
  }
);

const load_data_for_specific_status = async (status: FamiliarityStatus) => {
  switch (status) {
    case "trusted":
      await load_trusted_entities();
      break;
    case "unmanaged":
      await load_unmanaged_entities();
      break;
    case "untrusted":
    default:
      await load_untrusted_entities();
      break;
  }
};

const investigate_entity = async (entity: FamiliarityItem) => {
  SharedState.investigation.investigate({
    id: entity.internalEntityId,
    name: entity.email,
    email: entity.email,
    // @ts-ignore
    type: selected_entity_type.value.id,
  });
};

let is_init = false;
const init = async (payload: {
  info_type_id: string;
  sensitivity_level: number;
  entity_type: POPULATION_TYPE;
}) => {
  if (!is_init) {
    info_types.value = SharedState.l0InfoTypes.value
      .filter(
        // @ts-ignore
        (d) => d.id !== SharedState.default_label_id.value && !d.is_restricted
      )
      .map(({ id, labelName, shortName, code, l0Order }) => {
        return {
          checked: false,
          id,
          order: l0Order || 0,
          icon: ICONS.L0_ICONS.get(code),
          name: shortName || labelName,
        };
      })
      .sort((a, b) => a.order - b.order);

    const {
      entity_type = POPULATION_TYPE.EMPLOYEE,
      info_type_id = info_types.value[0].id,
      sensitivity_level = 3,
    } = payload;
    // @ts-ignore
    selected_info_type.value = info_types.value.find(
      (f) => f.id === info_type_id
    );
    // @ts-ignore
    selected_entity_type.value = entity_types.value.find(
      (f) => f.id === entity_type
    );
    // @ts-ignore
    selected_sensitivity_level.value = sensitivity_levels.value.find(
      // @ts-ignore
      (f) => f.id === sensitivity_level
    );
  }

  await on_filters_updated();
  is_init = true;
};

const current_tab: Ref<FamiliarityStatus> = ref("unmanaged");
const change_current_tab = async (tab: FamiliarityStatus) => {
  if (current_tab.value === tab) return;
  current_tab.value = tab;
};

// @ts-ignore
const sort_by_affinity = (a, b) => b.affinity - a.affinity;

const filter_by_affinity_level = (item: any) => {
  if (selected_affinity_level.value?.id === "none") {
    return true;
  } else {
    return item.level === selected_affinity_level.value?.id;
  }
};

const current_display_list_without_filtering = computed(() => {
  if (isSearching.value) {
    return unmanaged_entities_display_list.value;
  }
  switch (current_tab.value) {
    case "trusted":
      return trusted_entities.value;
    /*  case "trustedAsViewerOnly":
      return viewer_only_entities.value; */
    case "unmanaged":
      return [...unmanaged_entities_display_list.value];
    case "untrusted":
    default:
      return untrusted_entities.value;
  }
});

const current_display_list = computed(() => {
  if (isSearching.value) {
    return unmanaged_entities_display_list.value;
  }
  return current_display_list_without_filtering.value
    .filter(filter_by_affinity_level)
    .sort(sort_by_affinity);
});

const load_more_items = async () => {
  switch (current_tab.value) {
    case "trusted":
      if (has_more_trusted_results_to_load.value) {
        await load_trusted_entities();
        return;
      }
      break;
    case "unmanaged":
      if (has_more_unmanaged_results_to_load.value) {
        await load_unmanaged_entities();
        return;
      }
      break;
    case "untrusted":
    default:
      if (has_more_untrusted_results_to_load.value) {
        await load_untrusted_entities();
        return;
      }
      break;
  }
  console.log("no more entities to load");
};

const manage_trust = async (item: FamiliarityItem) => {
  console.log("manage_trust: ", item);
  await UseModal().show(ManageTrustModalVue, {
    entity: {
      ...item,
      name: item.email,
      id: item.entityId,
      type: selected_entity_type.value.id,
    },
    info_types: [selected_info_type.value.id],
  });
};

const show_events_modal = async (entity: FamiliarityItem) => {
  // @ts-ignore
  const axis_type = {
    [POPULATION_TYPE.EMPLOYEE]: AxisType.Employee,
    [POPULATION_TYPE.ORGANIZATION]: AxisType.OrganizationReceiver,
    [POPULATION_TYPE.PRIVATE_EMAIL]: AxisType.PersonalAddress,
  }[selected_entity_type.value.id] as AxisType;
  const { type, from } = SharedState.selectedTimeFrameOption.value;
  await UseModal().show(EventsModalVue, {
    title: `Events of <b>${entity.email}</b> which involved <b>${selected_info_type.value.name}</b> information`,
    files_title: `<b>${selected_info_type.value.name}</b> information files which <b>${entity.email}</b> have access`,
    coordinates: [
      {
        x: {
          type: axis_type,
          values: [entity.entityId],
        },
        y: {
          type: AxisType.ContentLabel,
          values: [selected_info_type.value.id],
        },
        z: {
          type,
          values: [from],
        },
      },
    ] as CoordinateInputTier3[],
  } as EventsModalProps);
};

const get_item_from_current_list = (id: string) => {
  switch (current_tab.value) {
    case "trusted": {
      const entity = trusted_entities.value.find(
        (d) => d.entityId == id
      ) as FamiliarityItem;
      return {
        entity,
        remove: () => {
          trusted_entities.value = trusted_entities.value.filter(
            (f) => f.entityId !== id
          );
          trusted_entities_count.value -= 1;
        },
      };
    }
    case "unmanaged": {
      const entity = unmanaged_entities.value.find((d) => d.entityId == id);
      return {
        entity,
        remove: () => {
          unmanaged_entities.value = unmanaged_entities.value.filter(
            (f) => f.entityId !== id
          );
          unmanaged_entities_count.value -= 1;
        },
      };
    }
    case "untrusted":
    case "trustedAsViewerOnly": {
      const entity = untrusted_entities.value.find((d) => d.entityId == id);
      return {
        entity,
        remove: () => {
          untrusted_entities.value = untrusted_entities.value.filter(
            (f) => f.entityId !== id
          );
          untrusted_entities_count.value -= 1;
        },
      };
    }
  }
};

const update_entity_status = async (payload: {
  entity_type: FamiliarityEntityType;
  entity_id: string;
  l0_id: string;
  threshold: number;
}) => {
  console.log(payload);
  const { entity_id, l0_id, threshold, entity_type } = payload;
  const v_label_id = SharedState.virtualLabels.value.find(
    (f) =>
      f.parentId === l0_id &&
      f.sensitivity == SensitivityLevelHelper.intToFloat(+threshold)
  )?.id as string;

  const status = threshold == -1 ? "unmanaged" : "trusted";
  await api.change_entity_familiarity_status({
    entity_type,
    entity_id,
    label_id: v_label_id,
    status,
  });

  const {
    entity,
    remove: remove_entity_from_original_list,
  } = get_item_from_current_list(payload.entity_id);

  const is_gte_selected_sensitivity =
    +selected_sensitivity_level.value.id <= +threshold;

  if (status === "unmanaged") {
    remove_entity_from_original_list();
    // add to unmanaged
    unmanaged_entities.value = [
      ...unmanaged_entities.value,
      entity as FamiliarityItem,
    ];
    unmanaged_entities_count.value += 1;
  } else {
    // add to trusted
    if (is_gte_selected_sensitivity) {
      const is_exist_in_trusted_list =
        trusted_entities.value.findIndex(
          (f) => f.entityId === payload.entity_id
        ) > -1;
      if (is_exist_in_trusted_list) {
        // don't do anything
      } else {
        remove_entity_from_original_list();
        // add to unmanaged
        trusted_entities.value = [
          ...trusted_entities.value,
          entity as FamiliarityItem,
        ];
        trusted_entities_count.value += 1;
      }
    } else {
      const is_exist_in_untrusted_list =
        untrusted_entities.value.findIndex(
          (f) => f.entityId === payload.entity_id
        ) > -1;
      if (is_exist_in_untrusted_list) {
        // don't do anything
      } else {
        remove_entity_from_original_list();
        // add to unmanaged
        untrusted_entities.value = [
          ...untrusted_entities.value,
          entity as FamiliarityItem,
        ];
        untrusted_entities_count.value += 1;
      }
    }
  }
  EventBus.emit("updated_trust_counters", {
    label: selected_sensitivity_level.value,
    type: selected_entity_type.value.id,
  });
};

export const state = {
  is_loading_all,
  // just_added_id,
  init,
  // info types
  info_types,
  selected_info_type,
  set_selected_info_type,
  selected_virtual_label,
  sub_info_types_for_label,
  // affinity filter
  affinity_levels,
  selected_affinity_level,
  set_selected_affinity_level,
  // departments
  departments,
  set_selected_department,
  selected_department,
  // entity type
  entity_types,
  selected_entity_type,
  set_selected_entity_type,
  // sensitivity
  sensitivity_levels,
  selected_sensitivity_level,
  set_selected_sensitivity_level,
  // unmanaged
  is_loading_unmanaged_entities: computed(
    () => is_loading_unmanaged_entities.value || isSearching.value
  ),
  unmanaged_entities_display_list,
  unmanaged_entities_count,
  load_unmanaged_entities,
  // trusted
  trusted_entities,
  trusted_entities_count,
  is_loading_trusted_entities,
  load_trusted_entities,
  // untrusted
  untrusted_entities,
  untrusted_entities_count,
  is_loading_untrusted_entities,
  load_untrusted_entities,
  load_more_items,
  query,
  // filter change
  is_filter_changed,
  on_filters_updated,
  // update entity status
  update_entity_status,
  investigate_entity,
  // nav tab
  current_tab,
  change_current_tab,
  current_display_list_without_filtering,
  current_display_list,
  //  update_status,
  // actions
  manage_trust,
  show_events_modal,
};
